import React from 'react';
import { CheckCircleOutline, Schedule } from '@mui/icons-material';
import { StatusChip } from '../ui';
import type { TranslateFn } from '../../i18n/useAppTranslation';

export type SaleWindowDisplayStatus = 'active' | 'upcoming' | 'expired' | 'cancelled';

/** Active, upcoming, expired or cancelled, as the store and the vendor both read it. */
export const saleWindowDisplayStatus = (
  w: { startAt: string; endAt: string; status: 'active' | 'expired' | 'cancelled' },
  now: Date = new Date(),
): SaleWindowDisplayStatus => {
  if (w.status !== 'active') return w.status;
  if (new Date(w.endAt).getTime() < now.getTime()) return 'expired';
  return new Date(w.startAt).getTime() > now.getTime() ? 'upcoming' : 'active';
};

/** One status chip in the shared status vocabulary. `ns` is the i18n namespace holding `status.*`. */
export const saleWindowChip = (status: SaleWindowDisplayStatus, t: TranslateFn, ns: string) => {
  const label = t(`${ns}.status.${status}`);
  if (status === 'active') return <StatusChip label={label} tone="positive" size="small" icon={<CheckCircleOutline />} />;
  if (status === 'upcoming') return <StatusChip label={label} tone="info" size="small" icon={<Schedule />} />;
  return <StatusChip label={label} tone="neutral" size="small" />;
};

/** "Nov 28, 2026 – Dec 1, 2026" in the viewer's locale. */
export const saleWindowRange = (startAt: string, endAt: string): string =>
  `${new Date(startAt).toLocaleDateString()} – ${new Date(endAt).toLocaleDateString()}`;

/** What a window lets a dealer go down to, in words. */
export const saleWindowFloorText = (
  w: { floorPrice: number | null; maxPercentBelowMap: number | null; floorHidden?: boolean },
  t: TranslateFn,
  ns: string,
  formatCurrency: (n: number) => string,
): string => {
  if (w.floorHidden) return t(`${ns}.floor.hidden`);
  const parts: string[] = [];
  if (w.floorPrice != null) parts.push(t(`${ns}.floor.price`, { price: formatCurrency(w.floorPrice) }));
  if (w.maxPercentBelowMap != null) parts.push(t(`${ns}.floor.percent`, { percent: w.maxPercentBelowMap }));
  return parts.length > 0 ? parts.join(' · ') : t(`${ns}.floor.none`);
};

/** "Nikon Zf (ZF)": a product a window names, as both views show it. */
export const saleWindowProductLabel = (p: { name: string; sku: string | null }): string =>
  p.sku && p.sku !== p.name ? `${p.name} (${p.sku})` : p.name;

/**
 * Which products a window covers, in words (MOT-370): every product of the
 * brand, or the first few it names and how many more. `ns` holds `products.*`.
 */
export const saleWindowProductsText = (
  w: { brandName: string; products: Array<{ name: string; sku: string | null }> | null },
  t: TranslateFn,
  ns: string,
  shown = 3,
): string => {
  if (!w.products || w.products.length === 0) return t(`${ns}.products.all`, { brand: w.brandName });
  const list = w.products.slice(0, shown).map(saleWindowProductLabel).join(', ');
  const more = w.products.length - shown;
  return more > 0 ? t(`${ns}.products.more`, { list, count: more }) : t(`${ns}.products.some`, { list });
};

/**
 * Whether moving a window's product list from `before` to `after` drops a
 * product it covered (null or empty = the whole brand), mirroring the server's
 * rule (MOT-415): whole brand to a list, or a list losing a product. Narrowing
 * ends the store prices the window admitted for the dropped products.
 */
export const saleWindowNarrows = (before: string[] | null | undefined, after: string[] | null | undefined): boolean => {
  if (!after || after.length === 0) return false;
  if (!before || before.length === 0) return true;
  const kept = new Set(after);
  return before.some((id) => !kept.has(id));
};

export interface SaleWindowScheduleEvent<W> {
  key: string;
  kind: 'opens' | 'closes';
  at: string;
  window: W;
}

/**
 * The scheduled changes a store's windows make (MOT-370): the start of every
 * window not yet open and the end of every window not yet closed, soonest
 * first. Pure, so the scheduled-changes tab and its test read the same list.
 */
export const saleWindowScheduleEvents = <W extends { id: string; startAt: string; endAt: string }>(
  windows: W[],
  now: Date = new Date(),
): SaleWindowScheduleEvent<W>[] => {
  const nowMs = now.getTime();
  const events: SaleWindowScheduleEvent<W>[] = [];
  for (const w of windows) {
    if (new Date(w.startAt).getTime() > nowMs) events.push({ key: `${w.id}:opens`, kind: 'opens', at: w.startAt, window: w });
    if (new Date(w.endAt).getTime() >= nowMs) events.push({ key: `${w.id}:closes`, kind: 'closes', at: w.endAt, window: w });
  }
  return events.sort(
    (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime() || (a.kind === b.kind ? 0 : a.kind === 'opens' ? -1 : 1),
  );
};
